import { logger } from '@shared/lib/utils/logger'
/**
 * Credentials storage for remember me functionality
 * Stores the credential type (email/phone) and value
 */

export interface SavedCredentials {
  type: 'email' | 'phone'
  value: string
}

const CREDENTIALS_STORAGE_KEY = 'professionals_saved_credentials'

/**
 * Centralized credentials management in localStorage
 * Used for "remember me" functionality
 */
export const credentialsStorage = {
  /**
   * Save credentials to localStorage
   */
  setCredentials(credentials: SavedCredentials): void {
    try {
      localStorage.setItem(CREDENTIALS_STORAGE_KEY, JSON.stringify(credentials))
    } catch (error) {
      logger.error('Failed to save credentials to localStorage:', error)
    }
  },

  /**
   * Get credentials from localStorage
   */
  getCredentials(): SavedCredentials | null {
    try {
      const stored = localStorage.getItem(CREDENTIALS_STORAGE_KEY)
      return stored ? (JSON.parse(stored) as SavedCredentials) : null
    } catch (error) {
      logger.error('Failed to load credentials from localStorage:', error)
      return null
    }
  },

  /**
   * Remove credentials from localStorage
   */
  clearCredentials(): void {
    try {
      localStorage.removeItem(CREDENTIALS_STORAGE_KEY)
    } catch (error) {
      logger.error('Failed to clear credentials from localStorage:', error)
    }
  },

  /**
   * Check if credentials exist in localStorage
   */
  hasCredentials(): boolean {
    return !!this.getCredentials()
  },

  /**
   * Verify logout was successful by checking if user data was cleared
   * NOTE: credentials are intentionally NOT cleared - they persist for "Remember Me" functionality
   * Only the user session should be cleared
   */
  verifyLogout(): void {
    const user = localStorage.getItem('professionals_user')

    if (user) {
      logger.error('[credentialsStorage] WARNING: User data still in localStorage after logout!', {
        hasUser: !!user,
      })
      // Force clear user if it didn't work
      localStorage.removeItem('professionals_user')
    }
  },
}
